// Precondition: None
// Postcondition: Custom element classes for timeline and timeline-item registered

class Timeline extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  connectedCallback(): void {
    this.render();
  }

  private render(): void {
    const style = document.createElement("style");
    style.textContent = this.getStyles();

    const template = document.createElement("template");
    template.innerHTML = this.getTemplate();

    if (this.shadowRoot) {
      this.shadowRoot.appendChild(style);
      this.shadowRoot.appendChild(template.content.cloneNode(true));
    }
  }

  private getStyles(): string {
    return `
      :host {
        --gutter: 28px;
        --marker-inset: 24px;

        display: block;
        position: relative;
        padding-left: var(--gutter);
      }

      :host::before {
        content: "";
        position: absolute;
        left: calc(var(--gutter) - var(--marker-inset) + 4px);
        top: 4px;
        bottom: 0;
        width: 2px;
        background: var(--timeline-line, #e0e0e0);
      }

      ::slotted(ft-timeline-item) {
        --timeline-item-dot-left: calc(-1 * var(--marker-inset));
      }

      @media (max-width: 639px) {
        :host {
          --gutter: 16px;
        }
      }
    `;
  }

  private getTemplate(): string {
    return `
      <div role="list" aria-label="Timeline">
        <slot></slot>
      </div>
    `;
  }
}

class TimelineItem extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  static get observedAttributes(): string[] {
    return ["period", "period-id"];
  }

  connectedCallback(): void {
    this.render();
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (oldValue !== newValue && this.shadowRoot) {
      if (name === "period" || name === "period-id") {
        const h2 = this.shadowRoot.querySelector("h2");
        const article = this.shadowRoot.querySelector("article");
        if (h2) {
          h2.textContent = this.period;
          if (this.periodId) h2.id = this.periodId;
        }
        if (article && this.periodId) article.setAttribute("aria-labelledby", this.periodId);
      }
    }
  }

  get period(): string {
    return this.getAttribute("period") || "";
  }

  get periodId(): string {
    return this.getAttribute("period-id") || "";
  }

  set period(value: string) {
    this.setAttribute("period", value);
  }

  set periodId(value: string) {
    this.setAttribute("period-id", value);
  }

  private render(): void {
    const style = document.createElement("style");
    style.textContent = this.getStyles();

    const template = document.createElement("template");
    template.innerHTML = this.getTemplate();

    if (this.shadowRoot) {
      this.shadowRoot.innerHTML = "";
      this.shadowRoot.appendChild(style);
      this.shadowRoot.appendChild(template.content.cloneNode(true));
    }
  }

  private getStyles(): string {
    return `
      :host {
        display: block;
        position: relative;
        margin: 24px 0 31px;
      }

      :host::before {
        content: "";
        position: absolute;
        left: var(--timeline-item-dot-left, -22px);
        top: 4px;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: var(--primary, #e0524d);
        box-shadow: 0 0 0 3px var(--primary-shadow, rgba(224, 82, 77, 0.25));
      }

      h2 {
        font: 700 18px/1.2 Space Grotesk, Inter, sans-serif;
        margin: 0 0 10px;
        color: var(--heading, #111827);
      }

      .content {
        display: flex;
        gap: 10px;
        flex-wrap: wrap;
        color: var(--text, #1f2937);
      }

      @media (prefers-color-scheme: dark) {
        h2 {
          color: var(--heading, #ffffff);
        }

        .content {
          color: var(--text, #f3f4f6);
        }
      }
    `;
  }

  private getTemplate(): string {
    return `
      <article role="listitem" aria-labelledby="${this.periodId}">
        <h2 id="${this.periodId}">${this.period}</h2>
        <div class="content" role="region" aria-label="Timeline content for ${this.period}">
          <slot></slot>
        </div>
      </article>
    `;
  }
}

// Register custom elements (no export statement)

customElements.define("ft-timeline", Timeline);

customElements.define("ft-timeline-item", TimelineItem);
